import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, Printer, Send, Download, Loader2, Image, 
  Upload, X, Plus, GripVertical
} from 'lucide-react';
import { API } from '@/App';

// Goodacres brand colors
const BRAND = {
  gold: '#c9a962',
  dark: '#1a1a1a',
  gray: '#666666',
  lightGray: '#f5f5f5'
};

// Helper to get full photo URL
const getPhotoUrl = (url) => {
  if (!url) return null;
  // If already full URL, return as-is
  if (url.startsWith('http')) return url;
  // Add API prefix for relative URLs
  return `${API}${url.startsWith('/') ? '' : '/'}${url}`;
};

export const PropertyBrochurePage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const printRef = useRef();
  const coverInputRef = useRef();
  const floorplanInputRef = useRef();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [particulars, setParticulars] = useState(null);
  const [brochure, setBrochure] = useState({
    cover_image: '',
    photos: [],
    floorplan_image: '',
    property_name: '',
    location: '',
    price: 0,
    price_qualifier: '',
    key_features: [],
    description_page2: '',  // For page 2 - max 800 chars
    description_page3: '',  // For page 3 - max 1200 chars
    epc_rating: '',
    bedrooms: '',
    bathrooms: '',
    receptions: '',
    property_type: '',
    floor_area_sqft: '',
    floor_area_sqm: ''
  });

  // Character limits
  const CHAR_LIMITS = {
    description_page2: 800,
    description_page3: 1200,
    feature: 60
  };
  
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchData();
  }, [caseId]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Get case data, particulars, photos, floorplans, and marketing details all at once
      const [caseRes, partRes, photosRes, brochureRes] = await Promise.all([
        axios.get(`${API}/sales/cases/${caseId}`, { headers }),
        axios.get(`${API}/documents/particulars/case/${caseId}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/documents/photos/case/${caseId}`, { headers }).catch(() => ({ data: { photos: [], floorplans: [] } })),
        axios.get(`${API}/documents/brochure/case/${caseId}`, { headers }).catch(() => ({ data: null }))
      ]);
      
      const caseInfo = caseRes.data.case;
      setCaseData(caseInfo);
      
      // Get photos and floorplans from photos tab
      const uploadedPhotos = photosRes.data.photos || [];
      const uploadedFloorplans = photosRes.data.floorplans || [];
      
      // Build photo URLs
      const photoUrls = uploadedPhotos.map(p => p.file_url);
      const mainPhoto = uploadedPhotos.find(p => p.is_main_photo);
      const floorplanUrl = uploadedFloorplans.length > 0 ? uploadedFloorplans[0].file_url : '';
      
      if (partRes.data) {
        setParticulars(partRes.data);
        // Pre-fill brochure from particulars + marketing details + photos
        setBrochure(prev => ({
          ...prev,
          property_name: caseInfo.address_line1 || caseInfo.address || '',
          location: `${caseInfo.postcode || ''}, ${caseInfo.county || 'Bedfordshire'}`,
          price: caseInfo.asking_price || caseInfo.offer_price || 0,
          key_features: caseInfo.features || partRes.data.features || [],
          full_description: caseInfo.full_description || partRes.data.full_description || '',
          description_page2: caseInfo.summary || partRes.data.summary || '',
          description_page3: caseInfo.full_description || partRes.data.full_description || '',
          location_description: partRes.data.location_description || '',
          epc_rating: caseInfo.epc_rating || partRes.data.epc_rating || '',
          bedrooms: caseInfo.bedrooms || partRes.data.bedrooms || '',
          bathrooms: caseInfo.bathrooms || partRes.data.bathrooms || '',
          receptions: caseInfo.reception_rooms || partRes.data.reception_rooms || '',
          property_type: caseInfo.property_type || partRes.data.property_type || '',
          floor_area_sqft: partRes.data.floor_area_sqft || '',
          floor_area_sqm: partRes.data.floor_area_sqm || '',
          // Photos from photos tab
          cover_image: mainPhoto?.file_url || (photoUrls.length > 0 ? photoUrls[0] : ''),
          photos: photoUrls,
          floorplan_image: floorplanUrl,
        }));
      } else {
        // Just use case data + photos
        setBrochure(prev => ({
          ...prev,
          property_name: caseInfo.address_line1 || caseInfo.address || '',
          location: `${caseInfo.postcode || ''}, ${caseInfo.county || 'Bedfordshire'}`,
          price: caseInfo.asking_price || caseInfo.offer_price || 0,
          key_features: caseInfo.features || [],
          description_page2: caseInfo.summary || '',
          description_page3: caseInfo.full_description || '',
          bedrooms: caseInfo.bedrooms || '',
          bathrooms: caseInfo.bathrooms || '',
          receptions: caseInfo.reception_rooms || '',
          property_type: caseInfo.property_type || '',
          epc_rating: caseInfo.epc_rating || '',
          // Photos from photos tab
          cover_image: mainPhoto?.file_url || (photoUrls.length > 0 ? photoUrls[0] : ''),
          photos: photoUrls,
          floorplan_image: floorplanUrl,
        }));
      }
      
      // Override with existing brochure data if saved
      if (brochureRes.data) {
        setBrochure(prev => ({ ...prev, ...brochureRes.data }));
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/documents/brochure/save`, {
        case_id: caseId,
        ...brochure
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Brochure saved');
    } catch (error) {
      toast.error('Failed to save brochure');
    } finally {
      setSaving(false);
    }
  };

  const handleSendForApproval = async () => {
    if (!caseData?.seller_email) {
      toast.error('No seller email found. Please add seller contact details first.');
      return;
    }
    
    setSending(true);
    try {
      // First save the brochure
      await axios.post(`${API}/documents/brochure/save`, {
        case_id: caseId,
        ...brochure
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Then send for approval
      await axios.post(`${API}/documents/brochure/send-for-approval`, {
        case_id: caseId,
        seller_email: caseData.seller_email,
        seller_name: caseData.seller_name || 'Seller',
        property_address: brochure.property_name || caseData.address_line1
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Preview sent to ${caseData.seller_email} for approval!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send for approval');
    } finally {
      setSending(false);
    }
  };

  const handlePrint = () => {
    setShowPreview(true);
    setTimeout(() => {
      window.print();
    }, 500);
  };

  const addFeature = () => {
    setBrochure(prev => ({
      ...prev,
      key_features: [...prev.key_features, '']
    }));
  };

  const updateFeature = (index, value) => {
    const updated = [...brochure.key_features];
    updated[index] = value;
    setBrochure(prev => ({ ...prev, key_features: updated }));
  };

  const removeFeature = (index) => {
    setBrochure(prev => ({
      ...prev,
      key_features: prev.key_features.filter((_, i) => i !== index)
    }));
  };

  // File upload handler
  const uploadFile = async (file, type = 'photo') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('case_id', caseId);
    formData.append('type', type);
    
    try {
      setUploading(true);
      const response = await axios.post(`${API}/documents/brochure/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data.url;
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload image');
      return null;
    } finally {
      setUploading(false);
    }
  };

  // Handle cover image upload
  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = await uploadFile(file, 'cover');
      if (url) {
        setBrochure(prev => ({ ...prev, cover_image: url }));
        toast.success('Cover image uploaded');
      }
    }
  };

  // Handle floorplan upload
  const handleFloorplanUpload = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = await uploadFile(file, 'floorplan');
      if (url) {
        setBrochure(prev => ({ ...prev, floorplan_image: url }));
        toast.success('Floorplan uploaded');
      }
    }
  };

  // Handle photo gallery upload
  const handlePhotoUpload = async (e, index) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = await uploadFile(file, 'photo');
      if (url) {
        const updated = [...brochure.photos];
        updated[index] = url;
        setBrochure(prev => ({ ...prev, photos: updated }));
        toast.success('Photo uploaded');
      }
    }
  };

  // Handle drag and drop for photos
  const handleDrop = useCallback(async (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const url = await uploadFile(file, 'photo');
      if (url) {
        const updated = [...brochure.photos];
        updated[index] = url;
        setBrochure(prev => ({ ...prev, photos: updated }));
        toast.success('Photo uploaded');
      }
    }
  }, [brochure.photos, caseId, token]);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const formatPrice = (price) => {
    if (!price) return '£POA';
    return `£${Number(price).toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // Preview/Print Mode
  if (showPreview) {
    return (
      <div className="bg-white">
        <div className="print:hidden fixed top-4 right-4 z-50 flex gap-2">
          <Button variant="outline" onClick={() => setShowPreview(false)}>
            Close Preview
          </Button>
          <Button onClick={() => window.print()}>
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
        </div>
        
        {/* Page 1: Cover */}
        <div className="w-[210mm] min-h-[297mm] mx-auto bg-white relative page-break-after">
          <div className="relative h-[297mm]">
            {brochure.cover_image ? (
              <img 
                src={getPhotoUrl(brochure.cover_image)} 
                alt="Property" 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center">
                <p className="text-slate-500">No cover image</p>
              </div>
            )}
            {/* Bottom overlay */}
            <div className="absolute bottom-0 left-0 right-0 bg-white/90 p-8 flex justify-between items-end">
              <div>
                <h1 className="text-3xl font-bold text-slate-800 uppercase tracking-wide">
                  {brochure.property_name}
                </h1>
                <p className="text-lg text-slate-600 mt-1">{brochure.location}</p>
              </div>
              <div className="text-right">
                <div className="w-16 h-16 flex items-center justify-center" style={{ backgroundColor: '#1a1a1a' }}>
                  <span className="text-3xl font-serif text-white">G</span>
                </div>
                <p className="text-xs text-slate-600 mt-1 tracking-widest">GOODACRES</p>
              </div>
            </div>
          </div>
        </div>

        {/* Page 2: Details & Features */}
        <div className="w-[210mm] min-h-[297mm] mx-auto bg-white p-8 page-break-after">
          <div className="grid grid-cols-2 gap-8 h-full">
            {/* Left: Photos */}
            <div className="space-y-4">
              {brochure.photos[0] && (
                <img src={getPhotoUrl(brochure.photos[0])} alt="Photo 1" className="w-full h-64 object-cover" />
              )}
              <div className="grid grid-cols-2 gap-4">
                {brochure.photos[1] && (
                  <img src={getPhotoUrl(brochure.photos[1])} alt="Photo 2" className="w-full h-40 object-cover" />
                )}
                {brochure.photos[2] && (
                  <img src={getPhotoUrl(brochure.photos[2])} alt="Photo 3" className="w-full h-40 object-cover" />
                )}
              </div>
            </div>
            
            {/* Right: Details */}
            <div>
              <h2 className="text-2xl font-bold text-slate-800">{brochure.property_name}</h2>
              <p className="text-slate-600">{brochure.location}</p>
              
              <p className="text-3xl font-bold text-slate-800 mt-4">
                {brochure.price_qualifier} {formatPrice(brochure.price)}
              </p>
              
              <div className="mt-6">
                <ul className="space-y-2">
                  {brochure.key_features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-700">
                      <span className="text-slate-400">•</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Page 2 description - character limited */}
              {brochure.description_page2 && (
                <div className="mt-6 text-sm text-slate-700 leading-relaxed">
                  {brochure.description_page2.split('\n\n').map((para, i) => (
                    para && <p key={i} className="mb-2">{para}</p>
                  ))}
                </div>
              )}
              
              {brochure.epc_rating && (
                <div className="mt-6 inline-block text-white px-4 py-2" style={{ backgroundColor: '#1a1a1a' }}>
                  <span className="text-sm">Energy Efficiency Rating {brochure.epc_rating}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Page 3: Description */}
        <div className="w-[210mm] min-h-[297mm] mx-auto bg-white p-8 page-break-after">
          <div className="grid grid-cols-2 gap-8">
            {/* Left: Main photo */}
            <div className="space-y-4">
              {brochure.photos[3] && (
                <img src={getPhotoUrl(brochure.photos[3])} alt="Photo 4" className="w-full h-80 object-cover" />
              )}
              <div className="grid grid-cols-2 gap-4">
                {brochure.photos[4] && (
                  <img src={getPhotoUrl(brochure.photos[4])} alt="Photo 5" className="w-full h-40 object-cover" />
                )}
                {brochure.photos[5] && (
                  <img src={getPhotoUrl(brochure.photos[5])} alt="Photo 6" className="w-full h-40 object-cover" />
                )}
              </div>
            </div>
            
            {/* Right: Description - using character-limited field */}
            <div className="text-sm text-slate-700 leading-relaxed space-y-4">
              {(brochure.description_page3 || '').split('\n\n').map((para, i) => (
                para && <p key={i}>{para}</p>
              ))}
            </div>
          </div>
        </div>

        {/* Page 4: Photo Gallery */}
        <div className="w-[210mm] min-h-[297mm] mx-auto bg-white p-8 page-break-after">
          <div className="grid grid-cols-2 gap-4">
            {brochure.photos.slice(6, 10).map((photo, i) => (
              <img key={i} src={getPhotoUrl(photo)} alt={`Photo ${i + 7}`} className="w-full h-64 object-cover" />
            ))}
          </div>
        </div>

        {/* Page 5: Floorplan */}
        <div className="w-[210mm] min-h-[297mm] mx-auto bg-white p-8 flex flex-col">
          {/* Measurements */}
          {(brochure.floor_area_sqft || brochure.floor_area_sqm) && (
            <div className="text-right text-sm text-slate-600 mb-4">
              {brochure.floor_area_sqft && (
                <p>Approximate Area = {brochure.floor_area_sqft} sq ft / {brochure.floor_area_sqm} sq m</p>
              )}
              <p className="text-xs">For identification only - Not to scale</p>
            </div>
          )}
          
          {/* Floorplan */}
          <div className="flex-1 flex items-center justify-center">
            {brochure.floorplan_image ? (
              <img 
                src={getPhotoUrl(brochure.floorplan_image)} 
                alt="Floorplan" 
                className="max-w-full max-h-[200mm] object-contain"
              />
            ) : (
              <div className="w-full h-64 bg-slate-100 flex items-center justify-center text-slate-400">
                No floorplan uploaded
              </div>
            )}
          </div>
          
          {/* Footer - Matt Black Banner */}
          <div className="text-white p-6 mt-auto -mx-8 -mb-8" style={{ backgroundColor: '#1a1a1a' }}>
            <div className="flex justify-between items-center">
              <div>
                <p className="font-bold">GOODACRES KEMPSTON</p>
                <p className="text-sm mt-2">3 High Street, Kempston, Bedford, MK42 7BT</p>
                <p className="text-sm">E: info@goodacresresidential.co.uk | T: 01234 855532</p>
              </div>
              <div className="text-center">
                <p className="text-sm">WWW.GOODACRESRESIDENTIAL.CO.UK</p>
              </div>
              <div className="text-right">
                <div className="w-16 h-16 border-2 border-white flex items-center justify-center">
                  <span className="text-3xl font-serif">G</span>
                </div>
                <p className="text-xs mt-1 tracking-widest">GOODACRES</p>
              </div>
            </div>
          </div>
        </div>
        
        <style>{`
          @media print {
            .page-break-after { page-break-after: always; }
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        `}</style>
      </div>
    );
  }

  // Edit Mode
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-800">Property Brochure</h1>
              <p className="text-sm text-slate-500">{caseData?.address_line1}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Draft
            </Button>
            <Button variant="outline" onClick={() => setShowPreview(true)}>
              Preview
            </Button>
            <Button onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" />
              Print / PDF
            </Button>
            <Button 
              onClick={handleSendForApproval}
              disabled={sending}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send for Approval
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-6 space-y-6">
        {/* Cover Image */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold mb-4">Cover Image</h2>
          <div 
            className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center cursor-pointer hover:border-amber-400 transition-colors"
            onClick={() => !brochure.cover_image && coverInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDrop={async (e) => {
              e.preventDefault();
              const file = e.dataTransfer.files?.[0];
              if (file && file.type.startsWith('image/')) {
                const url = await uploadFile(file, 'cover');
                if (url) {
                  setBrochure(prev => ({ ...prev, cover_image: url }));
                  toast.success('Cover image uploaded');
                }
              }
            }}
          >
            <input 
              type="file" 
              ref={coverInputRef} 
              className="hidden" 
              accept="image/*"
              onChange={handleCoverUpload}
            />
            {brochure.cover_image ? (
              <div className="relative">
                <img src={getPhotoUrl(brochure.cover_image)} alt="Cover" className="max-h-64 mx-auto rounded" />
                <Button 
                  variant="destructive" 
                  size="sm" 
                  className="absolute top-2 right-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    setBrochure(prev => ({ ...prev, cover_image: '' }));
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div>
                {uploading ? (
                  <Loader2 className="w-12 h-12 text-amber-500 mx-auto mb-2 animate-spin" />
                ) : (
                  <Upload className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                )}
                <p className="text-slate-600 font-medium">Drop image here or click to upload</p>
                <p className="text-slate-400 text-sm mt-1">PNG, JPG up to 10MB</p>
              </div>
            )}
          </div>
        </div>

        {/* Property Details */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold mb-4">Property Details</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-600">Property Name</label>
              <input
                type="text"
                value={brochure.property_name}
                onChange={(e) => setBrochure(prev => ({ ...prev, property_name: e.target.value }))}
                className="w-full p-2 border rounded mt-1"
                placeholder="e.g., Carnoustie Drive"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600">Location</label>
              <input
                type="text"
                value={brochure.location}
                onChange={(e) => setBrochure(prev => ({ ...prev, location: e.target.value }))}
                className="w-full p-2 border rounded mt-1"
                placeholder="e.g., Bedfordshire, MK40 4FE"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600">Price</label>
              <input
                type="number"
                value={brochure.price}
                onChange={(e) => setBrochure(prev => ({ ...prev, price: Number(e.target.value) }))}
                className="w-full p-2 border rounded mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600">Price Qualifier</label>
              <select
                value={brochure.price_qualifier}
                onChange={(e) => setBrochure(prev => ({ ...prev, price_qualifier: e.target.value }))}
                className="w-full p-2 border rounded mt-1"
              >
                <option value="">None</option>
                <option value="Price:">Price:</option>
                <option value="Guide Price:">Guide Price:</option>
                <option value="Offers Over:">Offers Over:</option>
                <option value="Offers in Region of:">Offers in Region of:</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600">EPC Rating</label>
              <select
                value={brochure.epc_rating}
                onChange={(e) => setBrochure(prev => ({ ...prev, epc_rating: e.target.value }))}
                className="w-full p-2 border rounded mt-1"
              >
                <option value="">Select...</option>
                {['A', 'B', 'C', 'D', 'E', 'F', 'G'].map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600">Floor Area (sq ft)</label>
              <input
                type="text"
                value={brochure.floor_area_sqft}
                onChange={(e) => setBrochure(prev => ({ ...prev, floor_area_sqft: e.target.value }))}
                className="w-full p-2 border rounded mt-1"
              />
            </div>
          </div>
        </div>

        {/* Key Features */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Key Features</h2>
            <Button variant="outline" size="sm" onClick={addFeature}>
              <Plus className="w-4 h-4 mr-1" /> Add Feature
            </Button>
          </div>
          <div className="space-y-2">
            {brochure.key_features.map((feature, i) => (
              <div key={i} className="flex items-center gap-2">
                <GripVertical className="w-4 h-4 text-slate-300" />
                <input
                  type="text"
                  value={feature}
                  onChange={(e) => updateFeature(i, e.target.value)}
                  className="flex-1 p-2 border rounded"
                  placeholder="Enter feature..."
                />
                <Button variant="ghost" size="sm" onClick={() => removeFeature(i)}>
                  <X className="w-4 h-4 text-slate-400" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Description */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold mb-4">Property Description</h2>
          
          {/* Page 2 Description */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-slate-600">Page 2 Description (beside photos)</label>
              <span className={`text-xs ${(brochure.description_page2?.length || 0) > CHAR_LIMITS.description_page2 ? 'text-red-500' : 'text-slate-400'}`}>
                {brochure.description_page2?.length || 0} / {CHAR_LIMITS.description_page2}
              </span>
            </div>
            <textarea
              value={brochure.description_page2 || ''}
              onChange={(e) => {
                if (e.target.value.length <= CHAR_LIMITS.description_page2) {
                  setBrochure(prev => ({ ...prev, description_page2: e.target.value }));
                }
              }}
              className="w-full p-3 border rounded min-h-[120px]"
              placeholder="Brief property introduction for page 2 (max 800 characters)..."
              maxLength={CHAR_LIMITS.description_page2}
            />
          </div>
          
          {/* Page 3 Description */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-slate-600">Page 3 Description (full details)</label>
              <span className={`text-xs ${(brochure.description_page3?.length || 0) > CHAR_LIMITS.description_page3 ? 'text-red-500' : 'text-slate-400'}`}>
                {brochure.description_page3?.length || 0} / {CHAR_LIMITS.description_page3}
              </span>
            </div>
            <textarea
              value={brochure.description_page3 || ''}
              onChange={(e) => {
                if (e.target.value.length <= CHAR_LIMITS.description_page3) {
                  setBrochure(prev => ({ ...prev, description_page3: e.target.value }));
                }
              }}
              className="w-full p-3 border rounded min-h-[180px]"
              placeholder="Detailed property description for page 3 (max 1200 characters)..."
              maxLength={CHAR_LIMITS.description_page3}
            />
          </div>
        </div>

        {/* Photo Gallery */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold mb-4">Photo Gallery (up to 10 images) - Drag & Drop</h2>
          <div className="grid grid-cols-5 gap-4">
            {[...Array(10)].map((_, i) => (
              <div 
                key={i} 
                className="aspect-square border-2 border-dashed border-slate-200 rounded-lg overflow-hidden cursor-pointer hover:border-amber-400 transition-colors"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, i)}
                onClick={() => {
                  if (!brochure.photos[i]) {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.onchange = (e) => handlePhotoUpload(e, i);
                    input.click();
                  }
                }}
              >
                {brochure.photos[i] ? (
                  <div className="relative w-full h-full group">
                    <img src={getPhotoUrl(brochure.photos[i])} alt={`Photo ${i+1}`} className="w-full h-full object-cover" />
                    <Button
                      variant="destructive"
                      size="sm"
                      className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation();
                        const updated = [...brochure.photos];
                        updated.splice(i, 1);
                        setBrochure(prev => ({ ...prev, photos: updated }));
                      }}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                    <div className="absolute bottom-1 left-1 bg-black/50 text-white text-xs px-1 rounded">
                      {i + 1}
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 hover:text-amber-500">
                    <Upload className="w-6 h-6 mb-1" />
                    <span className="text-xs">{i + 1}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
          <p className="text-sm text-slate-500 mt-2">Click or drag images to upload. Photos 1-3 appear on page 2, 4-6 on page 3, 7-10 on page 4.</p>
        </div>

        {/* Floorplan */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold mb-4">Floorplan</h2>
          <div 
            className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center cursor-pointer hover:border-amber-400 transition-colors"
            onClick={() => !brochure.floorplan_image && floorplanInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDrop={async (e) => {
              e.preventDefault();
              const file = e.dataTransfer.files?.[0];
              if (file && file.type.startsWith('image/')) {
                const url = await uploadFile(file, 'floorplan');
                if (url) {
                  setBrochure(prev => ({ ...prev, floorplan_image: url }));
                  toast.success('Floorplan uploaded');
                }
              }
            }}
          >
            <input 
              type="file" 
              ref={floorplanInputRef} 
              className="hidden" 
              accept="image/*"
              onChange={handleFloorplanUpload}
            />
            {brochure.floorplan_image ? (
              <div className="relative">
                <img src={getPhotoUrl(brochure.floorplan_image)} alt="Floorplan" className="max-h-64 mx-auto" />
                <Button
                  variant="destructive"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    setBrochure(prev => ({ ...prev, floorplan_image: '' }));
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div>
                {uploading ? (
                  <Loader2 className="w-12 h-12 text-amber-500 mx-auto mb-2 animate-spin" />
                ) : (
                  <Upload className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                )}
                <p className="text-slate-600 font-medium">Drop floorplan here or click to upload</p>
                <p className="text-slate-400 text-sm mt-1">PNG, JPG up to 10MB</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PropertyBrochurePage;
